import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTableState } from 'react-table'
import classNames from 'classnames'

import Table from '../Table'
import LoadingIcon from '../LoadingIcon'
import UnauthorisedMessage from '../UnauthorisedMessage'
import ServerErrorMessage from '../ServerErrorMessage'

import * as S from './styled'

const HydraTable = ({
    onFetchData,
    onFiltersChange = filters => filters,
    state,
    selectedColumns = {},
    defaultFilters = false,
    alwaysFilter = {},
    showFilterToggle = true,
    initialData = [],
    fixedRowCount,
    nestedTable = false,
    allowDuplicate = false,
    ...props
}) => {
    const [loading, setLoading] = useState(true)
    const [unauthorised, setUnauthorised] = useState(false)
    const [serverError, setServerError] = useState(false)
    const [data, setData] = useState(initialData)
    const [numberOfRows, setNumberOfRows] = useState(0)
    const [currentFilters, setCurrentFilters] = useState({})
    const [selectedFilters, setSelectedFilters] = useState([])

    const path = window.location.pathname
    const claims = window.localStorage.getItem('claims')
    const user = claims ? JSON.parse(claims)['id'] : ''

    const storedRPP = window.localStorage.getItem(`${user}_pageSize`)
    const rpp = fixedRowCount ? fixedRowCount : storedRPP ? storedRPP : 25

    const tableState = useTableState({ pageIndex: 0, pageSize: rpp })
    const [{ pageIndex, pageSize, filters }] = tableState

    const [pageCount, setPageCount] = useState(0)

    const onApplyFilters = () => {
        window.localStorage.setItem(`${user}_filterValues${path}`, JSON.stringify(filters))
        setCurrentFilters(onFiltersChange(filters))
    }

    const onResetFilters = () => {
        window.localStorage.removeItem(`${user}_filterValues${path}`)
        setCurrentFilters(defaultFilters ? defaultFilters : {})
    }

    const fetchData = async (state = null) => {
        let storedFilters = window.localStorage.getItem(`${user}_filterValues${path}`)
        let filterValues = {}

        if (storedFilters) {
            storedFilters = JSON.parse(storedFilters)
            filterValues = onFiltersChange(storedFilters)
        } else if (defaultFilters && Object.keys(currentFilters).length === 0) {
            filterValues = onFiltersChange(defaultFilters)
        } else {
            filterValues = currentFilters
        }

        setLoading(true)

        let sort = {}

        if (state && state.sortBy.length > 0) {
            sort = {
                field: state.sortBy[0].id,
                order: state.sortBy[0].desc ? 'desc' : 'asc',
            }
        }

        Object.keys(filterValues).forEach(val => {
            if (filterValues[val]?.length === 0) {
                delete filterValues[val]
            } else {
                if (typeof filterValues[val] === 'string') {
                    const splitVal = filterValues[val]?.split('.')

                    // Convert any currency values to integers
                    if (
                        splitVal?.length === 2 &&
                        isNumeric(splitVal[0]) &&
                        isNumeric(splitVal[1]) &&
                        splitVal[1]?.length < 3
                    ) {
                        filterValues[val] = filterValues[val].toString().replaceAll('.', '')

                        if (filterValues[val].startsWith('0')) {
                            filterValues[val] = filterValues[val].substring(1)
                        }
                    }
                }
            }
        })

        filterValues = {
            ...filterValues,
            ...alwaysFilter,
        }

        try {
            const { data, total } = await onFetchData({
                pagination: {
                    page: pageIndex + 1,
                    perPage: pageSize,
                },
                filter: filterValues,
                sort,
            })

            setCurrentFilters(currentFilters)
            setData(data)
            setNumberOfRows(total)
            setPageCount(Math.ceil(total / pageSize))
        } catch (e) {
            if (e?.status === 403) {
                setUnauthorised(true)
            } else if (e?.status >= 500) {
                setServerError(true)
            }
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (!loading) {
            fetchData()
        }
    }, [currentFilters, pageSize])

    const tableProps = {
        ...props,
        columns: useMemo(() => props.columns),
        onApplyFilters,
        onResetFilters,
        pageCount,
        data,
        numberOfRows,
        state: tableState,
        fetchData,
        selectedColumns,
        currentFilters,
        selectedFilters,
        setSelectedFilters,
        defaultFilters,
        loading,
        showFilterToggle,
        fixedRowCount,
        allowDuplicate: allowDuplicate,
    }

    if (unauthorised) {
        return <UnauthorisedMessage />
    }

    if (serverError) {
        return <ServerErrorMessage />
    }

    return (
        <S.HydraTableContainer>
            <Table {...tableProps} />
            <S.Loading
                className={classNames({
                    loading,
                    nestedTable,
                    '!pt-12': !showFilterToggle,
                })}
            >
                {loading && <LoadingIcon />}
            </S.Loading>
        </S.HydraTableContainer>
    )
}

HydraTable.propTypes = {
    onFetchData: PropTypes.func.isRequired,
}

function isNumeric(str) {
    if (typeof str !== 'string') return false
    return !isNaN(str) && !isNaN(parseFloat(str))
}

export default HydraTable
